import React, { useEffect, useState } from 'react';
import * as _ from 'lodash';
import { CommonEmptyDataV1 } from 'gemlux-common-components';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, Box, Button, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { SortOrder } from '../../../utils/enums/sort-order.enum';
import { RootState } from '../../../redux/store';
import AvatarEdit from '../../AvatarEdit/AvatarEdit';
import VisibilitySvg from '../../../assets/img/view.svg';
import TickSvg from '../../../assets/img/check-tick-02.svg';
import CloseIconCross from '../../../assets/img/cancel-circle.svg';
import ArchievedSvg from '../../../assets/img/delivery-box-02.svg';
import { theme } from '../../../theme/theme';
import { subordinateActions } from '../../../redux/subordinate/subordinateSlice';
import CustomTable, { ColumnProps } from '../../V1/CustomTable/CustomTable';
import SubordinatesDetails from '../SubordinatesDetails/SubordinatesDetails';
import DeleteSubordinateModal from '../SubordinatesDetails/DeleteSubordinateModal/DeleteSubordinateModal';
import EditSubordinateModal from '../SubordinatesDetails/EditSubordinateModal/EditSubordinateModal';
import useEnv from '../../../utils/use-env-hook';

const SubordinatesTable = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get('page');
  const rowsPerPageParam = searchParams.get('rows-per-page');
  const sortByParam = searchParams.get('sort-by');
  const sortOrderParam = searchParams.get('sort-order');
  const { baseUrl } = useEnv();
  const [page, setPage] = useState<number>(pageParam ? parseInt(pageParam, 10) : 1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageParam ? parseInt(rowsPerPageParam, 10) : 10);
  const [sortBy, setSortBy] = useState<string>(sortByParam ?? 'systemId');
  const [sortOrder, setSortOrder] = useState<SortOrder>((sortOrderParam as SortOrder) ?? SortOrder.DESC);

  const { data, total, isLoading, componentStatus, subordinate } = useSelector((state: RootState) => state.subordinate);

  const getSubordinateDetailsLoading = Boolean(_.get(componentStatus, 'getSubordinateDetailsLoading', false));
  const isViewSubordinateDialogOpen = componentStatus.viewSubordinateDialogOpen;

  const [selectedSubordinateId, setSelectedSubordinateId] = useState<string | null>(null);

  useEffect(() => {
    if (pageParam !== null) {
      setPage(parseInt(pageParam, 10));
    }
    if (rowsPerPageParam !== null) {
      setRowsPerPage(parseInt(rowsPerPageParam, 10));
    }
    if (sortByParam !== null) {
      setSortBy(sortByParam);
    }
    if (sortOrderParam !== null) {
      setSortOrder(sortOrderParam as SortOrder);
    }
  }, [searchParams, pageParam, rowsPerPageParam, sortByParam, sortOrderParam]);

  useEffect(() => {
    setSearchParams({
      page: page.toString(),
      'rows-per-page': rowsPerPage.toString(),
      'sort-by': sortBy,
      'sort-order': sortOrder,
    });
  }, [page, rowsPerPage, sortBy, sortOrder, setSearchParams]);

  useEffect(() => {
    dispatch(subordinateActions.fetchSubordinatesRequest({ page, limit: rowsPerPage, sortBy, sortOrder }));
  }, [dispatch, page, rowsPerPage, sortBy, sortOrder]);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    const totalPages = Math.ceil(total / newRowsPerPage);
    const newPage = Math.min(page, totalPages);
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const handleSortByChange = (newSortBy: string) => {
    setSortBy(newSortBy);
  };

  const handleSortOrderChange = (newSortOrder: SortOrder) => {
    setSortOrder(newSortOrder);
  };

  const handleViewSubordinateDetails = (subordinateId: string) => {
    setSelectedSubordinateId(subordinateId);
    dispatch(subordinateActions.openViewSubordinateDialog());
  };

  const handleCloseViewSubordinateDetails = () => {
    dispatch(subordinateActions.closeViewSubordinateDialog());
  };

  const getStatusIcon = (status: string): string | undefined => {
    switch (status.toLowerCase()) {
      case 'active':
        return TickSvg;
      case 'inactive':
        return CloseIconCross;
      case 'archived':
        return ArchievedSvg;
      default:
        return undefined;
    }
  };

  const getStatusStyle = (status: string) => {
    const baseStyles = {
      height: 'var(--24px, 24px)',
      padding: '4px 10px 4px 6px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '29px',
      gap: '4px',
    };

    switch (status.toLowerCase()) {
      case 'active':
        return {
          ...baseStyles,
          background: theme.palette.shades.successBrightLight,
        };
      case 'inactive':
        return {
          ...baseStyles,
          background: theme.palette.shades.errorBrightLight,
        };
      case 'archived':
        return {
          ...baseStyles,
          background: theme.palette.shades.warningBrightLight,
        };
      default:
        return baseStyles;
    }
  };

  const getStatusTextColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'active':
        return theme.palette.shades.successBrightMain;
      case 'inactive':
        return theme.palette.shades.errorBrightMain;
      case 'archived':
        return theme.palette.shades.warningBrightMain;
      default:
        return 'inherit';
    }
  };

  const columns: ColumnProps[] = [
    {
      key: 'userId',
      name: 'UserID',
      enableSort: true,
      align: 'left',
      width: '10%',
      resizable: false,
    },
    {
      key: 'name',
      name: 'Name',
      enableSort: true,
      align: 'left',
      width: '20%',
      resizable: false,
    },
    {
      key: 'email',
      name: 'Email Address',
      enableSort: false,
      align: 'left',
      width: '20%',
      resizable: false,
    },
    {
      key: 'status',
      name: 'Status',
      enableSort: true,
      align: 'left',
      width: '10%',
      resizable: false,
    },
    {
      key: 'noOfUploads',
      name: 'No. Of Gemstones',
      enableSort: true,
      align: 'left',
      width: '10%',
      resizable: false,
    },
    {
      key: 'lastLogin',
      name: 'Last Log-in',
      enableSort: true,
      align: 'left',
      width: '15%',
      resizable: false,
    },
    {
      key: 'userSince',
      name: 'User Since',
      enableSort: true,
      align: 'left',
      width: '10%',
      resizable: false,
    },
    {
      key: 'actions',
      name: 'Actions',
      enableSort: false,
      align: 'center',
      width: '5%',
      resizable: false,
    },
  ];
  const currentPageData = React.useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return data.slice(startIndex, endIndex);
  }, [page, rowsPerPage, data]);
  const rows = currentPageData.map((item: any) => ({
    userId: item.systemId || '00000',
    name: (
      <Box display="flex" alignItems="center" gap={2}>
        {item.avatar ? (
          <Avatar
            alt="Profile Image"
            src={`${baseUrl}/${item.avatar}`}
            sx={{ width: '28px', height: '28px', border: 'none', objectFit: 'cover', cursor: 'pointer' }}
          />
        ) : (
          <AvatarEdit
            editable={false}
            name={item.firstName}
            name2={item.familyName}
            font={theme.typography.footnoteBold}
            size="28px"
            data-testid="avatar"
          />
        )}
        <Typography variant="metaInter">
          {item.firstName} {item.familyName}
        </Typography>
      </Box>
    ),
    email: item.email,
    status: (
      <Box display="flex" alignItems="center" gap={1}>
        <Box
          sx={{
            ...getStatusStyle(item.status),
          }}
        >
          <Box component="img" src={getStatusIcon(item.status)} alt={item.status} sx={{ width: 16, height: 16 }} />
          <Typography variant="metaInter" sx={{ color: getStatusTextColor(item.status) }}>
            {item.status.charAt(0).toUpperCase() + item.status.slice(1).toLowerCase()}
          </Typography>
        </Box>
      </Box>
    ),
    noOfUploads: item.noOfUploads || '0',
    lastLogin: item.lastLogin ? dayjs(item.lastLogin).format('DD/MM/YYYY HH:mm:ss') : 'N/A',
    userSince: item.userSince ? dayjs(item.userSince).format('DD/MM/YYYY ') : 'N/A',
    actions: (
      <Box display="flex" justifyContent="center">
        <Button
          data-testid="subordinate-details"
          size="small"
          variant="text"
          onClick={() => handleViewSubordinateDetails(item.userId)}
          startIcon={
            <Box
              component="img"
              src={VisibilitySvg}
              alt="View"
              sx={{
                width: 20,
                height: 20,
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 },
              }}
            />
          }
        />
      </Box>
    ),
  }));

  useEffect(() => {
    if (selectedSubordinateId) {
      dispatch(subordinateActions.getSubordinatesDetails({ userId: selectedSubordinateId }));
    }
  }, [dispatch, selectedSubordinateId]);

  return (
    <>
      <Box mt={2}>
        {data.length === 0 && !isLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight="50vh">
            <CommonEmptyDataV1
              title="No Subordinates!"
              description="Once subordinates are added, they will be displayed here."
            />
          </Box>
        ) : (
          <CustomTable
            isLoading={isLoading}
            totalDataRows={total}
            enablePagination
            selectedPage={page}
            enablePagePerRows
            rowsPerList={[10, 20, 50, 100]}
            selectedRowsPerPage={rowsPerPage}
            sortBy={sortBy}
            sortOrder={sortOrder}
            columns={columns}
            rows={rows}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleSortByChange={handleSortByChange}
            handleSortOrderChange={handleSortOrderChange}
            tableType="Subordinates"
          />
        )}
      </Box>

      {isViewSubordinateDialogOpen && (
        <SubordinatesDetails
          handleClose={handleCloseViewSubordinateDetails}
          subordinate={subordinate}
          open={isViewSubordinateDialogOpen}
          isLoading={getSubordinateDetailsLoading}
        />
      )}
      <DeleteSubordinateModal />
      <EditSubordinateModal />
    </>
  );
};

export default SubordinatesTable;
